import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "nm_cart_v1";

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};

const VIDEO_EXTS = [".mp4", ".webm", ".mov", ".ogv", ".m4v"];
function isVideoUrl(url) {
  if (!url) return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  return VIDEO_EXTS.some((ext) => clean.endsWith(ext));
}

// items: [{ product_id, title, price, discount, image, quantity }]
export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (e) {
      console.warn("cart: failed to read from localStorage", e);
    }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }
    catch (e) { console.warn("cart: failed to persist to localStorage", e); }
  }, [items]);

  const add = useCallback((product) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.product_id === product.id);
      if (existing) {
        return prev.map((p) =>
          p.product_id === product.id ? { ...p, quantity: p.quantity + 1 } : p
        );
      }
      // Priority for the cart thumbnail:
      //   1. Admin-set cart_thumbnail on the product
      //   2. First image (not video) in the product gallery
      //   3. Empty — CartDrawer will fall back to the site logo
      let image = (product.cart_thumbnail || "").trim();
      if (!image) {
        const firstImage = (product.images || []).find(
          (img) => img?.url && !isVideoUrl(img.url)
        );
        image = firstImage?.url || "";
      }
      return [
        ...prev,
        {
          product_id: product.id,
          title: product.title,
          price: product.price || "",
          discount: product.discount || 0,
          image,
          quantity: 1,
        },
      ];
    });
    setOpen(true);
  }, []);

  const remove = useCallback((product_id) => {
    setItems((prev) => prev.filter((p) => p.product_id !== product_id));
  }, []);

  const updateQty = useCallback((product_id, qty) => {
    const q = Math.max(1, parseInt(qty, 10) || 1);
    setItems((prev) => prev.map((p) => (p.product_id === product_id ? { ...p, quantity: q } : p)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((a, i) => a + i.quantity, 0), [items]);
  const has = useCallback((product_id) => items.some((i) => i.product_id === product_id), [items]);

  const value = useMemo(
    () => ({ items, open, setOpen, add, remove, updateQty, clear, count, has }),
    [items, open, setOpen, add, remove, updateQty, clear, count, has]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
